"use client"
import type { Timeframe } from "@opensea/graphql"
import type { NumberFormatDisplay } from "@opensea/i18n"
import { useIsLessThanMd } from "@opensea/ui-kit"
import { StatsSortBy } from "@opensea/utils"
import { useTranslations } from "next-intl"
import { STATS_TABLE_COLUMN_CLASSNAMES } from "./StatsTable/constants"

type StatsColumn = {
  key: string
  header: (timeframe: Timeframe) => string
  sortKey?: StatsSortBy
  isAffectedByTimeframe?: boolean
  isChange?: boolean
  display?: NumberFormatDisplay
  tooltip?: (timeframe: Timeframe) => string
  isCompact?: boolean
  className?: string
}

const useTimeFrameHeaderPrefix = () => {
  const t = useTranslations("useStatsColumns")

  return (timeframe: Timeframe) => {
    switch (timeframe) {
      case "ONE_MINUTE":
        return {
          sales: {
            display: t("oneMinute.sales.display"),
            tooltip: t("oneMinute.sales.tooltip"),
          },
          vol: {
            display: t("oneMinute.vol.display"),
            tooltip: t("oneMinute.vol.tooltip"),
          },
          change: {
            display: t("oneMinute.change.display"),
            tooltip: t("oneMinute.change.tooltip"),
          },
        }
      case "FIFTEEN_MINUTE":
        return {
          sales: {
            display: t("fifteenMinutes.sales.display"),
            tooltip: t("fifteenMinutes.sales.tooltip"),
          },
          vol: {
            display: t("fifteenMinutes.vol.display"),
            tooltip: t("fifteenMinutes.vol.tooltip"),
          },
          change: {
            display: t("fifteenMinutes.change.display"),
            tooltip: t("fifteenMinutes.change.tooltip"),
          },
        }
      case "FIVE_MINUTE":
        return {
          sales: {
            display: t("fiveMinutes.sales.display"),
            tooltip: t("fiveMinutes.sales.tooltip"),
          },
          vol: {
            display: t("fiveMinutes.vol.display"),
            tooltip: t("fiveMinutes.vol.tooltip"),
          },
          change: {
            display: t("fiveMinutes.change.display"),
            tooltip: t("fiveMinutes.change.tooltip"),
          },
        }
      case "ONE_HOUR":
        return {
          sales: {
            display: t("oneHour.sales.display"),
            tooltip: t("oneHour.sales.tooltip"),
          },
          vol: {
            display: t("oneHour.vol.display"),
            tooltip: t("oneHour.vol.tooltip"),
          },
          change: {
            display: t("oneHour.change.display"),
            tooltip: t("oneHour.change.tooltip"),
          },
        }
      case "ONE_DAY":
        return {
          sales: {
            display: t("oneDay.sales.display"),
            tooltip: t("oneDay.sales.tooltip"),
          },
          vol: {
            display: t("oneDay.vol.display"),
            tooltip: t("oneDay.vol.tooltip"),
          },
          change: {
            display: t("oneDay.change.display"),
            tooltip: t("oneDay.change.tooltip"),
          },
        }
      case "SEVEN_DAYS":
        return {
          sales: {
            display: t("sevenDays.sales.display"),
            tooltip: t("sevenDays.sales.tooltip"),
          },
          vol: {
            display: t("sevenDays.vol.display"),
            tooltip: t("sevenDays.vol.tooltip"),
          },
          change: {
            display: t("sevenDays.change.display"),
            tooltip: t("sevenDays.change.tooltip"),
          },
        }
      case "THIRTY_DAYS":
        return {
          sales: {
            display: t("thirtyDays.sales.display"),
            tooltip: t("thirtyDays.sales.tooltip"),
          },
          vol: {
            display: t("thirtyDays.vol.display"),
            tooltip: t("thirtyDays.vol.tooltip"),
          },
          change: {
            display: t("thirtyDays.change.display"),
            tooltip: t("thirtyDays.change.tooltip"),
          },
        }
      default:
        return {
          sales: {
            display: t("allTime.sales.display"),
            tooltip: t("allTime.sales.tooltip"),
          },
          vol: {
            display: t("allTime.vol.display"),
            tooltip: t("allTime.vol.tooltip"),
          },
          change: {
            display: t("allTime.change.display"),
            tooltip: t("allTime.change.tooltip"),
          },
        }
    }
  }
}

export const useStatsColumns = (timeframe: Timeframe, portfolio?: boolean) => {
  const t = useTranslations("useStatsColumns")
  const timeframeHeaderPrefix = useTimeFrameHeaderPrefix()
  const isMobile = useIsLessThanMd()

  const valueColumn: StatsColumn = {
    key: "value",
    header: () => t("value"),
  }

  const topOfferColumn: StatsColumn = {
    key: "topOffer",
    header: () => t("topOffer.header"),
    sortKey: StatsSortBy.BEST_OFFER,
    tooltip: () => t("topOffer.tooltip"),
  }

  const floorPriceColumn: StatsColumn = {
    key: "floorPrice",
    header: () => t("floorPrice.header"),
    sortKey: StatsSortBy.FLOOR_PRICE,
    tooltip: () => t("floorPrice.tooltip"),
  }

  const volumeColumn: StatsColumn = {
    key: "volume",
    header: () => timeframeHeaderPrefix(timeframe).vol.display,
    sortKey: StatsSortBy.VOLUME,
    isAffectedByTimeframe: true,
    tooltip: () => timeframeHeaderPrefix(timeframe).vol.tooltip,
  }

  const salesColumn: StatsColumn = {
    key: "sales",
    header: () => timeframeHeaderPrefix(timeframe).sales.display,
    sortKey: StatsSortBy.SALES,
    isAffectedByTimeframe: true,
    display: "full",
    tooltip: () => timeframeHeaderPrefix(timeframe).sales.tooltip,
    isCompact: true,
  }

  const ownersColumn: StatsColumn = {
    key: "owners",
    header: () => t("owners.header"),
    display: "full",
    tooltip: () => t("owners.tooltip"),
  }

  const supplyColumn: StatsColumn = {
    key: "supply",
    header: () => t("supply.header"),
    display: "full",
    tooltip: () => t("supply.tooltip"),
  }

  const floorChangeColumn: StatsColumn = {
    key: "floorChange",
    header: () => timeframeHeaderPrefix(timeframe).change.display,
    sortKey: StatsSortBy.FLOOR_CHANGE,
    isAffectedByTimeframe: true,
    tooltip: () => timeframeHeaderPrefix(timeframe).change.tooltip,
    isCompact: true,
  }

  const lastSevenDaysColumn: StatsColumn = {
    key: "lastSevenDays",
    header: () => t("last7Days.header"),
    className: STATS_TABLE_COLUMN_CLASSNAMES.last7Days,
  }

  const floorChangeArr = timeframe !== "ALL_TIME" ? [floorChangeColumn] : []

  const portfolioColumns = [
    valueColumn,
    topOfferColumn,
    floorPriceColumn,
    ...floorChangeArr,
    volumeColumn,
    salesColumn,
    ownersColumn,
    supplyColumn,
    lastSevenDaysColumn,
  ] as const

  const statsColumns = [
    floorPriceColumn,
    ...floorChangeArr,
    topOfferColumn,
    volumeColumn,
    salesColumn,
    ownersColumn,
    supplyColumn,
    lastSevenDaysColumn,
  ] as const

  const statsColumnsMobile = [
    floorPriceColumn,
    lastSevenDaysColumn,
    topOfferColumn,
    volumeColumn,
    salesColumn,
    ownersColumn,
    supplyColumn,
  ] as const

  if (portfolio) {
    return portfolioColumns
  }

  if (isMobile) {
    return statsColumnsMobile
  }

  return statsColumns
}
